import { Injectable } from '@angular/core';
import { AbstractCRUD, BannerNotificationsService, ExceptionService, PaginatedList, UtilsService } from '../../xform-compat';
import { concat, Observable, of } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, last, map, mergeMap } from 'rxjs/operators';
import { StructCapCRMItem } from '../../shared/models/crm/struct-cap/StructCapCRMItem.model';
import { BaseCRMItem, CRMItem } from '../../shared/models/crm/CRMItem.model';
import { BaseCRMServiceInt } from './base-crm-service.interface';
import { CrmUtilsService } from './crm-utils.service';
import { DateTimeService } from '../services';
import { CONFIG } from 'src/app/config';
import { CRMItemHistory } from 'src/app/shared/models/crm/CRMItemHistory.model';
import { CRMAlarm } from '../../shared/models/crm/CRMAlarm.model';
import { CrmService } from './crm.service';
import { CrmAlarmUtilsService } from './crm-alarm-utils.service';
import { StructCapCrmStatusService } from './struct-cap-crm-status.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class StructCapCrmService extends AbstractCRUD<BaseCRMItem> implements BaseCRMServiceInt {

  constructor(
    exceptionService: ExceptionService,
    http: HttpClient,
    utilsService: UtilsService,
    private crmUtilsService: CrmUtilsService,
    private crmService: CrmService,
    private crmAlarmUtilsService: CrmAlarmUtilsService,
    private structCapCrmStatusService: StructCapCrmStatusService,
    private bannerNotificationService: BannerNotificationsService,
    private translateService: TranslateService
  ) {
    super(exceptionService, http, utilsService);
  }

  public getResourcePath(): string {
    return '/crm/structcapitem';
  }

  public getRecord(itemId: number): Observable<StructCapCRMItem> {
    return super.getHTTP().get(`${this.getApiPath()}/${itemId}`)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public getRecords(filter?: any): Observable<PaginatedList<StructCapCRMItem>> {
    const params = this.crmUtilsService.setRequestParams(filter);
    return super.getHTTP().get(`${this.getApiPath()}/all`, { params: params })
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public createRecord(record: any): Observable<any> {
    const params = new HttpParams().set('notify', record.shouldNotify);
    return super.getHTTP().post(`${this.getApiPath()}/create`, record, { params: params })
      .pipe(mergeMap((crm: any) => this.handleSilencedStatus(crm.id, crm, record.silenceUntil)))
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public updateRecord(record: any): Observable<any> {
    return super.getHTTP().put(`${this.getApiPath()}/${record.id}/update`, record)
      .pipe(mergeMap((crm: any) => this.handleSilencedStatus(crm.id, crm, record.silenceUntil)))
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public createNote(crmId: number, note: any, params?: any): Observable<any> {
    return super.getHTTP().post(`${this.getApiPath()}/${crmId}/notes`, note, { params: params })
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public addOwner(itemId: number, userId: number): Observable<any> {
    return super.getHTTP().put(`${this.getApiPath()}/${itemId}/owner/${userId}`,
      {}).pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public removeOwner(itemId: number, userId: number): Observable<any> {
    return super.getHTTP().put(`${this.getApiPath()}/${itemId}/owner/${userId}/remove`,
      {}).pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public getCrmItemHistory(crmId: number): Observable<any> {
    return super.getHTTP().get(`${this.getApiPath()}/${crmId}/audit`)
    .pipe(map((response: Array<CRMItemHistory>) => response.map(r => ({
      ...r,
      eventTimestamp: DateTimeService.convertToEst(`${r.eventTimestamp}`,
        CONFIG.formatEST, CONFIG.dateTimeServerFormat)
    }))))
    .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public getFilterOptions(filter?: any): Observable<Map<string, Array<string>>> {
    const params = this.crmUtilsService.setRequestParams(filter);
    return super.getHTTP().get(`${this.getApiPath()}/all/options`, { params: params })
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public downloadRecords(filter?: any) {
    filter.page = 0;
    filter.pagesize = 9999999;
    const params = this.crmUtilsService.setRequestParams(filter);
    return super.getHTTP().get(`${this.getApiPath()}/export`, {params: params, responseType: 'text'})
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)))
      .subscribe((value: any) => {
          const hiddenElement = document.createElement('a');
          hiddenElement.href = 'data:attachment/csv,' + encodeURIComponent(value);
          hiddenElement.target = '_blank';
          hiddenElement.download = 'structured_capital_sonar.csv';
          document.body.appendChild(hiddenElement);
          hiddenElement.click();
          this.bannerNotificationService.success(this.translateService.instant('GENERICS.DOWNLOADING'));
        }, () => this.bannerNotificationService.error(this.translateService.instant('GENERICS.DOWNLOAD_ERROR'))
      );
  }

  /**
   * Guarantees the caller receives the saved CRM item. Persisting a silence alarm takes two extra
   * requests whose responses are alarms rather than items, so only the last emission is kept and it
   * is mapped back onto the item with the newly created alarm attached.
   */
  private handleSilencedStatus(crmId: number, crmData: any, silenceUntil: any): Observable<CRMItem> {
    const item: CRMItem = crmData;
    item.id = crmId;
    if (this.structCapCrmStatusService.isSilenceStatus(crmData.status) && silenceUntil) {
      return concat(
        this.crmService.removeSilenceAlarm(item),
        this.crmService.addAlarm(crmId, this.crmAlarmUtilsService.createSilenceAlarm(crmId, silenceUntil, crmData.status))
      ).pipe(last(), map((alarm: CRMAlarm) => {
        if (alarm) {
          item.alarms = [alarm];
        }
        return item;
      }));
    }
    return of(item);
  }

}
